import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  Chip
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';

/**
 * RoleAssignmentDialog Component
 *
 * Modal for changing a team member's role.
 *
 * Features:
 * - Role selection dropdown
 * - Role descriptions
 * - Permission validation
 * - Confirmation before saving
 *
 * Props:
 * - open: boolean - Dialog open state
 * - onClose: function - Close handler
 * - member: object - Member to edit { id, name, email, role }
 * - currentUserRole: string - Current user's role for permission checks
 * - onSave: function - Save handler (memberId, newRole)
 */
function RoleAssignmentDialog({ open, onClose, member, currentUserRole, onSave }) {
  const [selectedRole, setSelectedRole] = useState(member?.role || 'viewer');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Define available roles with descriptions and permissions (matching backend OrganizationRole enum)
  const roles = [
    {
      value: 'owner',
      label: 'Owner',
      description: 'Full control over the organization. Can delete organization and manage billing.',
      permissions: [
        'Manage billing and subscriptions',
        'Add/remove team members',
        'Assign/change roles',
        'Access all organization data',
        'Delete organization'
      ]
    },
    {
      value: 'admin',
      label: 'Admin',
      description: 'Can manage members, settings, and all resources. Cannot manage billing.',
      permissions: [
        'Add/remove team members (except owner)',
        'Assign roles (except owner)',
        'Edit organization settings',
        'Manage all missions and fleet',
        'Access all organization data'
      ]
    },
    {
      value: 'manager',
      label: 'Manager',
      description: 'Can manage missions and fleet, invite members. Limited admin access.',
      permissions: [
        'Manage missions and fleet',
        'Invite new members',
        'View team members',
        'Access organization data'
      ]
    },
    {
      value: 'pilot',
      label: 'Pilot',
      description: 'Can execute missions and view fleet. Standard operational access.',
      permissions: [
        'Execute missions',
        'View fleet',
        'Manage own operations',
        'View team members'
      ]
    },
    {
      value: 'viewer',
      label: 'Viewer',
      description: 'Read-only access to all data. Cannot modify or execute operations.',
      permissions: [
        'View all organization data',
        'View missions and fleet',
        'View team members'
      ]
    }
  ];

  // Reset state when dialog opens
  React.useEffect(() => {
    if (open && member) {
      setSelectedRole(member.role);
      setError('');
    }
  }, [open, member]);

  // Permission checks
  const canAssignOwner = currentUserRole === 'owner';
  const canAssignAdmin = currentUserRole === 'owner' || currentUserRole === 'admin';

  // Filter roles based on permissions
  const availableRoles = roles.filter(role => {
    if (role.value === 'owner' && !canAssignOwner) return false;
    if (role.value === 'admin' && !canAssignAdmin) return false;
    return true;
  });

  const handleRoleChange = (event) => {
    setSelectedRole(event.target.value);
    setError('');
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      // Validation
      if (!selectedRole) {
        setError('Please select a role');
        return;
      }

      if (selectedRole === member.role) {
        setError('No changes made. Please select a different role.');
        return;
      }

      // Special validation for owner role
      if (selectedRole === 'owner' && currentUserRole !== 'owner') {
        setError('Only the current owner can assign the owner role.');
        return;
      }

      // Call the save handler
      await onSave(member.id, selectedRole);

      // Close dialog on success
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to update role. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };

  const selectedRoleInfo = roles.find(r => r.value === selectedRole);

  if (!member) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <PersonIcon />
          <Typography variant="h6">Change Member Role</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Member Info */}
        <Box mb={3} p={2} bgcolor="#f5f5f5" borderRadius={1}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Changing role for:
          </Typography>
          <Typography variant="h6">{member.name}</Typography>
          <Typography variant="body2" color="textSecondary">
            {member.email}
          </Typography>
          <Box mt={1}>
            <Typography variant="caption" color="textSecondary">
              Current Role:{' '}
            </Typography>
            <Chip
              label={member.role.toUpperCase()}
              size="small"
              color={
                member.role === 'owner' ? 'error' :
                member.role === 'admin' ? 'warning' :
                member.role === 'manager' ? 'info' :
                member.role === 'pilot' ? 'success' :
                'default'
              }
              sx={{ borderRadius: '4px' }}
            />
          </Box>
        </Box>

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Role Selection */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>New Role</InputLabel>
          <Select
            value={selectedRole}
            onChange={handleRoleChange}
            label="New Role"
            disabled={saving}
          >
            {availableRoles.map((role) => (
              <MenuItem key={role.value} value={role.value}>
                {role.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Role Description */}
        {selectedRoleInfo && (
          <Box p={2} bgcolor="#f9f9f9" borderRadius={1}>
            <Typography variant="subtitle2" gutterBottom color="primary">
              {selectedRoleInfo.label} Permissions
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              {selectedRoleInfo.description}
            </Typography>
            <Box component="ul" sx={{ pl: 2, m: 0 }}>
              {selectedRoleInfo.permissions.map((permission, index) => (
                <Typography
                  key={index}
                  component="li"
                  variant="body2"
                  color="textSecondary"
                  sx={{ mb: 0.5 }}
                >
                  {permission}
                </Typography>
              ))}
            </Box>
          </Box>
        )}

        {/* Warning for Owner Transfer */}
        {selectedRole === 'owner' && member.role !== 'owner' && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Transferring Ownership
            </Typography>
            <Typography variant="body2">
              Assigning the owner role will transfer full ownership to {member.name}.
              Your role will be changed to Admin. This action should be done carefully.
            </Typography>
          </Alert>
        )}

        {/* Warning for Downgrading */}
        {(member.role === 'admin' || member.role === 'manager') &&
         (selectedRole === 'viewer' || selectedRole === 'pilot') && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Downgrading to {selectedRole} will remove administrative privileges.
              {member.name} will no longer be able to manage team members or organization settings.
            </Typography>
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={saving || selectedRole === member.role}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default RoleAssignmentDialog;
